import { memo } from 'react';
import { type Path, useFormContext, useWatch } from 'react-hook-form';
import { cn, Input } from '@/core/ui';
import type { IStationSchema } from './schema';

interface SecondsFieldProps {
  label: string;
  name: Path<IStationSchema>;
  errorMessage?: string;
}

const SecondsField = ({ label, name, errorMessage }: SecondsFieldProps) => {
  const { control, setValue } = useFormContext<IStationSchema>();
  const seconds = (useWatch({ control, name }) as number | undefined) ?? 0;

  const handleValueChange = (raw: string) => {
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed) || parsed < 1) return;
    setValue(name, parsed, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <Input
      label={label}
      type='number'
      min={1}
      variant='faded'
      value={seconds > 0 ? String(seconds) : ''}
      onValueChange={handleValueChange}
      isInvalid={Boolean(errorMessage)}
      errorMessage={errorMessage}
      endContent={<span className='text-xs text-default-400 pr-1'>sec</span>}
    />
  );
};

const _PhaseTimersForm = ({ className }: { className?: string }) => {
  const { formState } = useFormContext<IStationSchema>();
  const fieldError = formState.errors.phaseTimers;

  const introError = fieldError && 'introPhase' in fieldError ? fieldError.introPhase?.message : undefined;
  const readingError =
    fieldError && 'readingPhase' in fieldError ? fieldError.readingPhase?.message : undefined;
  const stationPhaseError =
    fieldError && 'stationPhase' in fieldError ? fieldError.stationPhase?.message : undefined;
  const examinerError =
    fieldError && 'examinerPhase' in fieldError ? fieldError.examinerPhase?.message : undefined;

  return (
    <section className={cn('rounded-md border border-default-200 p-4', className)}>
      <p className='text-sm font-semibold mb-3'>Phase timers</p>
      <div className='flex flex-col gap-3'>
        <SecondsField label='Intro phase' name='phaseTimers.introPhase' errorMessage={introError} />
        <SecondsField label='Reading timer' name='phaseTimers.readingPhase' errorMessage={readingError} />
        <SecondsField
          label='Station phase'
          name='phaseTimers.stationPhase'
          errorMessage={stationPhaseError}
        />
        <SecondsField label='Examiner timer' name='phaseTimers.examinerPhase' errorMessage={examinerError} />
      </div>
    </section>
  );
};

export const PhaseTimersForm = memo(_PhaseTimersForm);
